import { ProductFormulations } from '@montugroup/circuit-shared/types/index';

import type {
  RefillResponse,
  RefillResponseOrderProduct,
  RefillResponseProduct,
} from '@montugroup/circuit-shared/types/refill';

/** @deprecated - This should come from the backend */
export const getProductFilterTypeFromFormulationId = (id: number) => Object.values(ProductFormulations)[id - 1];

/** @deprecated - Non prescribable products should be derived differently */
export const getDeviceFormulationId = (): number => 5;

export const FORMULATION_ID_DEVICE = getDeviceFormulationId(); // Devices (e.g. vapes)

function sortByOrderProducts(orderProducts: RefillResponseOrderProduct[]) {
  return (a: RefillResponseProduct, b: RefillResponseProduct) => {
    const productBSortWeight = orderProducts.some((op) => op.id === b.id) ? 1 : 0;
    const productASortWeight = orderProducts.some((op) => op.id === a.id) ? 1 : 0;
    return productBSortWeight - productASortWeight || a.id - b.id;
  };
}

const isProductOutOfStock = (product: RefillResponseProduct) =>
  Boolean(product.is_out_of_stock || product.reasoning_toggle);

/**
 * Transforms the products array into three lists: non device prescription products, device prescription products, device non prescription products.
 * Each of these lists is then also also sorted
 * 1. Non device prescription products are sorted by all products found in orderProducts first, then products not found in orderProducts, then products that are interval locked and lastly products that are out of stock. Any product with no remaining units or where the prescription is expired is removed from this list.
 * 2. Device prescription products are sorted by all products found in orderProducts first, then products not found in orderProducts. This list does not include all devices avaiable to the patient only those that were prescribed.
 * 3. Device non prescription products are sorted by all products found in orderProducts first, then products not found in orderProducts. All out of stock products in this list are removed.
 * All three lists are then concatenated together and returned, in the order of non device prescription products, device prescription products, device non prescription products.
 * Diagram explaining this logic https://montugroup.atlassian.net/wiki/spaces/Eng/pages/191397901/Logic+for+displaying+products+on+V3
 */
export const transformProductList = (
  products: RefillResponseProduct[],
  orderProducts: RefillResponseOrderProduct[],
) => {
  // All non device prescription products sorted by those that are in orderProducts first
  const nonDevicePrescriptionProducts = products
    .filter(
      (product) =>
        product.formulation_id !== FORMULATION_ID_DEVICE &&
        product.remaining_units! > 0 &&
        product.is_expired === false,
    )
    .sort(sortByOrderProducts(orderProducts))
    .sort(
      (a, b) =>
        (a.intervalDetails?.estimatedIntervalState === 'LOCKED' ? 1 : 0) -
        (b.intervalDetails?.estimatedIntervalState === 'LOCKED' ? 1 : 0),
    )
    .sort((a, b) => (isProductOutOfStock(a) ? 1 : 0) - (isProductOutOfStock(b) ? 1 : 0));

  // All device prescription products
  const devicePrescriptionProducts = products
    .filter((product) => product.formulation_id === FORMULATION_ID_DEVICE && !!product.prescription_id)
    .sort(sortByOrderProducts(orderProducts))
    .sort((a, b) => (b.campaign_discount_price ? 1 : 0) - (a.campaign_discount_price ? 1 : 0));

  // All device non prescription products
  const deviceNonPrescriptionProducts = products
    .filter(
      (product) =>
        product.formulation_id === FORMULATION_ID_DEVICE && !product.prescription_id && !isProductOutOfStock(product),
    )
    .sort(sortByOrderProducts(orderProducts))
    .sort((a, b) => (b.campaign_discount_price ? 1 : 0) - (a.campaign_discount_price ? 1 : 0));

  return [...nonDevicePrescriptionProducts, ...devicePrescriptionProducts, ...deviceNonPrescriptionProducts];
};

export const deriveMaxAllowedQuantity = (availableProduct: RefillResponseProduct) => {
  if (availableProduct.formulation_id === FORMULATION_ID_DEVICE) {
    return 10;
  }

  const remainingUnits = Math.max(availableProduct.remaining_units!, 0);

  return Math.min(remainingUnits, availableProduct.quantity_base_order);
};

// eslint-disable-next-line no-undefined
export const nullToUndefined = (value: any) => (value === null ? undefined : value);

export const remapResponseRemainingUnits = (responseData: RefillResponse): RefillResponse => {
  let products: RefillResponseProduct[] = [];

  if (typeof responseData === 'object' && 'products' in responseData && Array.isArray(responseData.products)) {
    products = responseData.products.map((product) => {
      let remainingUnits = 0;
      if (
        product !== null &&
        typeof product === 'object' &&
        'remainingUnits' in product &&
        typeof product.remainingUnits === 'number'
      ) {
        remainingUnits = product.remainingUnits;
      }

      if (
        product !== null &&
        typeof product === 'object' &&
        'remaining_units' in product &&
        typeof product.remaining_units === 'number'
      ) {
        remainingUnits = product.remaining_units;
      }

      return {
        ...product,
        remaining_units: remainingUnits,
      };
    });
  }

  return {
    ...responseData,
    products,
  };
};

// These methods are exported purely for testing; Do not reference directly in production code.
// eslint-disable-next-line no-underscore-dangle
export const _testing = {
  isProductOutOfStock,
  deriveMaxAllowedQuantity,
};
